import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import logo from './assets/logo.png';
import wealth from './assets/wealth.png';
import { brand } from './portalTheme';
import { portalErrorMessage } from '../../../services/request/CustomerPortal';
import { useTranslation } from '../../../i18n';

export interface PortalOutletContext {
  customerName?: string;
  refreshUnread: () => void;
}

export function usePortalContext(): PortalOutletContext {
  return useOutletContext<PortalOutletContext>();
}

export interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: React.DependencyList): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then(result => {
        if (active) setData(result);
      })
      .catch(err => {
        if (active) setError(portalErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce(value => value + 1), []);
  return { data, loading, error, reload };
}

export function BrandMark({ size = 40 }: { size?: number }) {
  const { t } = useTranslation();
  return <Box component="img" src={logo} alt={t('common.logoAlt')} sx={{ width: size, height: size, borderRadius: '50%', display: 'block' }} />;
}

export function WealthGraphic({ size = 220 }: { size?: number }) {
  const { t } = useTranslation();
  return (
    <Box
      component="img"
      src={wealth}
      alt={t('common.wealthAlt')}
      sx={{ width: size, height: size, maxWidth: '100%', borderRadius: 4, display: 'block' }}
    />
  );
}

export function PageTitle({ kicker, title, subtitle, action }: { kicker?: string; title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      <Box>
        {kicker ? (
          <Typography variant="overline" sx={{ color: brand.gold }}>
            {kicker}
          </Typography>
        ) : null}
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle ? (
          <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Box>
  );
}

const STATUS_TONES: Record<string, { bg: string; fg: string }> = {
  ACTIVE: { bg: brand.positiveSoft, fg: brand.positive },
  APPROVED: { bg: brand.positiveSoft, fg: brand.positive },
  DISBURSED: { bg: brand.positiveSoft, fg: brand.positive },
  ONBOARDED: { bg: brand.positiveSoft, fg: brand.positive },
  CLOSED: { bg: brand.border, fg: brand.charcoal },
  PENDING: { bg: brand.warningSoft, fg: brand.warning },
  SUBMITTED: { bg: brand.warningSoft, fg: brand.warning },
  IN_REVIEW: { bg: brand.goldSoft, fg: brand.warning },
  DECLINED: { bg: brand.dangerSoft, fg: brand.danger },
  REJECTED: { bg: brand.dangerSoft, fg: brand.danger },
  WRITTEN_OFF: { bg: brand.dangerSoft, fg: brand.danger },
};

export function StatusChip({ status }: { status?: string }) {
  const { term } = useTranslation();
  const tone = STATUS_TONES[(status ?? '').toUpperCase()] ?? { bg: brand.goldSoft, fg: brand.charcoal };
  return <Chip size="small" label={term('status', status, '—')} sx={{ bgcolor: tone.bg, color: tone.fg }} />;
}

export function LoadingState({ label }: { label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 6, justifyContent: 'center', color: brand.slate }}>
      <CircularProgress size={22} sx={{ color: brand.gold }} />
      <Typography>{label}</Typography>
    </Box>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <Alert
      severity="error"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.tryAgain')}
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}

export function EmptyState({ title, message }: { title: string; message?: string }) {
  return (
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6">{title}</Typography>
      {message ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {message}
        </Typography>
      ) : null}
    </Paper>
  );
}

export function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.25, borderBottom: `1px solid ${brand.border}` }}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}
